import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createProduct, reset } from '../store/slices/productSlice';
import { useNavigate } from 'react-router-dom';
import './CreateProduct.css';

const MAX_IMAGES = 5;

const CreateProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, isError, message, isSuccess } = useSelector(s => s.products);

  const [title, setTitle] = useState('');
  const [categoriesText, setCategoriesText] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]); // File[]
  const [previews, setPreviews] = useState([]); // { url, name }
  const [localError, setLocalError] = useState('');
  const [submitted, setSubmitted] = useState(false); // track if this page initiated a create action

  // On mount clear any previous success flag (e.g., from list fetch) so we don't auto-redirect back
  useEffect(() => {
    dispatch(reset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate only if this form submitted AND creation succeeded
  useEffect(() => {
    if (submitted && isSuccess) {
      navigate('/products');
      dispatch(reset());
    }
  }, [submitted, isSuccess, navigate, dispatch]);

  useEffect(() => () => { // cleanup previews
    previews.forEach(p => URL.revokeObjectURL(p.url));
  }, [previews]);

  const onFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const next = [...files];
    const nextPreviews = [...previews];
    for (const file of selected) {
      if (next.length >= MAX_IMAGES) break;
      if (!['image/jpeg','image/png','image/webp'].includes(file.type)) {
        setLocalError('Unsupported image type – only JPEG/PNG/WEBP allowed');
        continue;
      }
      if (file.size > 3 * 1024 * 1024) {
        setLocalError('Image exceeds 3MB limit');
        continue;
      }
      next.push(file);
      nextPreviews.push({ url: URL.createObjectURL(file), name: file.name });
    }
    setFiles(next);
    setPreviews(nextPreviews);
    e.target.value = '';
  };

  const removeImage = (idx) => {
    const next = [...files];
    const nextPrev = [...previews];
    const removed = nextPrev[idx];
    if (removed) URL.revokeObjectURL(removed.url);
    next.splice(idx,1);
    nextPrev.splice(idx,1);
    setFiles(next);
    setPreviews(nextPrev);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!title.trim()) return setLocalError('Title is required');
    if (!basePrice || Number.isNaN(Number(basePrice))) return setLocalError('Valid base price required');
    const form = new FormData();
    form.append('title', title.trim());
    if (categoriesText.trim()) form.append('categories', categoriesText.trim());
    form.append('price', JSON.stringify({ base: Number(basePrice) }));
    form.append('inventory', JSON.stringify({ available: quantity ? Number(quantity) : 0 }));
    if (description.trim()) form.append('description', description.trim());
    files.forEach(f => form.append('images', f));
  if (files.length) form.append('_hasImages', 'true');
    try {
      setSubmitted(true);
      await dispatch(createProduct(form)).unwrap();
    } catch (err) {
      // handled by slice isError/message
    }
  };

  return (
    <div className="create-product page-wrapper">
      <div className="page-header">
        <h1>Create Product</h1>
        <p>Provide core details and images</p>
      </div>
      <form className="panel product-form" onSubmit={onSubmit}>
        <div className="form-grid">
          <div className="field-group">
            <label htmlFor="cp-title">Title *</label>
            <input id="cp-title" value={title} onChange={e => setTitle(e.target.value)} required />
          </div>
          <div className="field-group">
            <label htmlFor="cp-categories">Categories (comma)</label>
            <input id="cp-categories" value={categoriesText} onChange={e => setCategoriesText(e.target.value)} placeholder="e.g. Electronics, Accessories" />
          </div>
          <div className="field-group">
            <label htmlFor="cp-price">Base Price (USD) *</label>
            <input id="cp-price" type="number" step="0.01" value={basePrice} onChange={e => setBasePrice(e.target.value)} required />
          </div>
          <div className="field-group">
            <label htmlFor="cp-qty">Quantity Available</label>
            <input id="cp-qty" type="number" value={quantity} onChange={e => setQuantity(e.target.value)} />
          </div>
          <div className="field-group full">
            <label htmlFor="cp-desc">Description</label>
            <textarea id="cp-desc" rows={5} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div className="field-group full">
            <label>Images (up to {MAX_IMAGES})</label>
            <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={onFileChange} />
            {previews.length > 0 && (
              <div className="image-previews">
                {previews.map((p, idx) => (
                  <figure key={p.url}>
                    <img src={p.url} alt={p.name} />
                    <button type="button" className="remove" onClick={() => removeImage(idx)} aria-label="Remove image">×</button>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </div>
        {(localError || isError) && <div className="error-inline">{localError || message}</div>}
        <div className="form-actions">
          <button type="button" className="btn text" onClick={() => navigate('/products')} disabled={isLoading}>Cancel</button>
          <button type="submit" className="btn primary" disabled={isLoading}>{isLoading ? 'Creating...' : 'Create Product'}</button>
        </div>
      </form>
    </div>
  );
};

export default CreateProduct;
